import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Phone } from "lucide-react";
import axios from "axios";
import PropertyCard from "@/components/PropertyCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`).catch(() => {});
        const res = await axios.get(`${API}/properties`, { params: { featured: true, limit: 4 } });
        setFeatured(res.data.properties || []);
      } catch (e) {
        console.error("Failed to fetch featured properties:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchFeatured();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section
        data-testid="hero-section"
        className="relative min-h-screen flex items-center"
      >
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/f994a921-acd5-4ef7-a754-2485a6eee63b/images/5c8e38e0e2b5188eb64fc8c38a8d0d135866384487d9fbc1470a643db79bc80f.png')`,
          }}
        />
        <div className="absolute inset-0 bg-[#051C2C]/60" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 pt-20">
          <div className="max-w-2xl">
            <p
              className="font-cursive text-gold text-4xl sm:text-5xl mb-4 animate-fade-in-up"
              style={{ animationDelay: "0.1s" }}
            >
              Noor Realtor
            </p>
            <h1
              className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none text-beige mb-6 animate-fade-in-up"
              style={{ animationDelay: "0.3s" }}
            >
              Discover Luxury Living in South Goa
            </h1>
            <p
              className="text-base text-beige/70 leading-relaxed mb-10 max-w-lg animate-fade-in-up"
              style={{ animationDelay: "0.5s" }}
            >
              Premium villas, elegant apartments, and prime land plots
              curated for those who appreciate the finer things in life.
            </p>
            <div
              className="flex flex-col sm:flex-row gap-4 animate-fade-in-up"
              style={{ animationDelay: "0.7s" }}
            >
              <Link
                to="/properties"
                data-testid="hero-cta-button"
                className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-navy font-bold text-sm tracking-widest uppercase px-8 py-4 transition-colors duration-300"
              >
                View Properties <ArrowRight size={16} />
              </Link>
              <a
                href="https://wa.me/918483918695"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-button"
                className="inline-flex items-center justify-center gap-2 border border-gold/50 hover:border-gold text-gold text-sm tracking-widest uppercase px-8 py-4 transition-colors duration-300"
              >
                <Phone size={16} /> WhatsApp Us
              </a>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-fade-in" style={{ animationDelay: "1.2s" }}>
          <span className="text-xs tracking-[0.2em] uppercase text-beige/40">Scroll</span>
          <div className="w-px h-10 bg-gradient-to-b from-gold/50 to-transparent" />
        </div>
      </section>

      {/* Property Types */}
      <section className="py-24 px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="mb-16">
          <p className="text-xs tracking-[0.2em] uppercase text-gold font-bold mb-3">Our Portfolio</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-beige">
            Curated Properties in South Goa
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              type: "villa",
              title: "Luxury Villas",
              desc: "Private estates with world-class amenities",
              img: "https://images.unsplash.com/photo-1760943013869-65a30a4fafd1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjB2aWxsYSUyMGV4dGVyaW9yJTIwcG9vbHxlbnwwfHx8fDE3NzU3MjAxNzB8MA&ixlib=rb-4.1.0&q=85",
            },
            {
              type: "flat",
              title: "Premium Apartments",
              desc: "Elegant living spaces with stunning views",
              img: "https://images.pexels.com/photos/8135492/pexels-photo-8135492.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
            },
            {
              type: "land",
              title: "Prime Land Plots",
              desc: "Strategic locations for your dream project",
              img: "https://images.unsplash.com/photo-1661956337153-f12289363a31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxsdXNoJTIwZ3JlZW4lMjBsYW5kJTIwcmVhbCUyMGVzdGF0ZXxlbnwwfHx8fDE3NzU3MjAxNzB8MA&ixlib=rb-4.1.0&q=85",
            },
          ].map((cat) => (
            <Link
              key={cat.type}
              to={`/properties?type=${cat.type}`}
              data-testid={`category-${cat.type}`}
              className="group relative aspect-[3/4] overflow-hidden rounded-sm"
            >
              <img
                src={cat.img}
                alt={cat.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#051C2C]/90 via-[#051C2C]/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="font-heading text-xl text-beige mb-1">{cat.title}</h3>
                <p className="text-sm text-beige/60">{cat.desc}</p>
                <div className="mt-4 flex items-center gap-2 text-gold text-sm tracking-wider uppercase group-hover:gap-3 transition-all duration-300">
                  Explore <ArrowRight size={14} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-24 px-6 lg:px-8 bg-navy-light">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-16">
            <div>
              <p className="text-xs tracking-[0.2em] uppercase text-gold font-bold mb-3">Featured</p>
              <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-beige">
                Handpicked Selections
              </h2>
            </div>
            <Link
              to="/properties"
              data-testid="view-all-properties-link"
              className="hidden sm:flex items-center gap-2 text-gold text-sm tracking-wider uppercase hover:gap-3 transition-all duration-300"
            >
              View All <ArrowRight size={14} />
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-navy/50 rounded-sm animate-pulse aspect-[3/4]" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featured.slice(0, 3).map((property) => (
                <PropertyCard key={property.id} property={property} />
              ))}
            </div>
          )}

          <Link
            to="/properties"
            className="sm:hidden flex items-center justify-center gap-2 text-gold text-sm tracking-wider uppercase mt-8 hover:gap-3 transition-all duration-300"
          >
            View All Properties <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <p className="font-cursive text-gold text-3xl mb-4">Get in Touch</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight text-beige mb-6">
            Ready to Find Your Dream Property?
          </h2>
          <p className="text-beige/60 mb-10 max-w-lg mx-auto">
            Let us help you discover the perfect property in South Goa.
            Our team is ready to guide you through every step.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              data-testid="cta-contact-button"
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-navy font-bold text-sm tracking-widest uppercase px-8 py-4 transition-colors duration-300"
            >
              Contact Us
            </Link>
            <Link
              to="/schedule-visit"
              data-testid="cta-schedule-visit-button"
              className="inline-flex items-center justify-center gap-2 border border-gold/50 hover:border-gold text-gold text-sm tracking-widest uppercase px-8 py-4 transition-colors duration-300"
            >
              Schedule a Visit
            </Link>
          </div>
        </div>
      </section>

      {/* Location Bar */}
      <section className="border-t border-white/10 py-8 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-8 text-beige/40 text-xs tracking-[0.15em] uppercase">
          {["Benaulim", "Colva", "Palolem", "Varca", "Majorda", "Canacona"].map((loc) => (
            <span key={loc} className="flex items-center gap-1.5">
              <MapPin size={12} className="text-gold/50" /> {loc}
            </span>
          ))}
        </div>
      </section>
    </div>
  );
}
